import { PencilIcon, RefreshCwIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFetcher } from '@/data/fetcher'
import { CountrySelect } from '@/forms/components/CountrySelect'
import { UnifiedBusinessSearch } from '@/forms/components/UnifiedBusinessSearch'
import { DocumentFormValues } from '@/forms/DocumentForm'
import { useCountries } from '@/hooks/useCountries'
import { cn } from '@/lib/utils'
import { components } from '@invo.space/shared'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

export interface ParticipantFormProps {
  prefix: 'customer' | 'beneficiary' | 'supplier'
  isReadOnly?: boolean
  showAccountNumber?: boolean
  showDefaultBankAccount?: boolean
}

// export type ParticipantFormValues = z.infer<typeof participant>

export const ParticipantForm = ({
  prefix,
  isReadOnly = false,
  showAccountNumber = false,
  showDefaultBankAccount = false,
}: ParticipantFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'participant.form',
  })
  const methods = useFormContext<DocumentFormValues>()
  const { register, formState: { errors } } = methods

  const { data: bankAccountsData } = useSWR<{ data?: InvoicingBankAccount[] }>(
    showDefaultBankAccount ? '/api/v2/bank-accounts' : null,
    apiFetcher,
  )
  const bankAccounts = bankAccountsData?.data

  const name = useWatch({ control: methods.control, name: `${prefix}.name` })
  const hasName = !!(name ?? '').trim()
  const prefixErrors = errors[prefix]
  const hasDetailErrors =
    !!prefixErrors && Object.keys(prefixErrors).some((key) => key !== 'name' && key !== 'message' && key !== 'ref' && key !== 'type')

  // search: jen vyhledávací pole, summary: karta s vybraným kontaktem, manual: všechna pole
  const [mode, setMode] = useState<'search' | 'summary' | 'manual'>(() => (hasName ? 'summary' : 'search'))
  const nextModeRef = useRef<'summary' | 'manual' | null>(null)
  const [focusSearch, setFocusSearch] = useState(false)

  useEffect(() => {
    if (mode === 'search' && hasName) {
      setMode(nextModeRef.current ?? 'summary')
      nextModeRef.current = null
    }
  }, [mode, hasName])

  // chyba v detailu kontaktu (e-mail, telefon...) musí být vidět
  useEffect(() => {
    if (mode !== 'manual' && hasDetailErrors) setMode('manual')
  }, [mode, hasDetailErrors])

  const clearParticipant = () => {
    const country = methods.getValues(`${prefix}.address.country`) || 'CZ'
    methods.setValue(prefix, {
      id: undefined,
      name: '',
      businessId: '',
      taxId: '',
      email: '',
      phone: '',
      address: { address: '', zip: '', city: '', country },
    } as DocumentFormValues['customer'])
    nextModeRef.current = null
    setFocusSearch(true)
    setMode('search')
  }

  const nameError = errors[prefix]?.name?.message && (
    <p className="text-sm text-danger">{t(`name.errors.notEmpty`)}</p>
  )

  const search = (
    <UnifiedBusinessSearch
      prefix={prefix}
      isReadOnly={isReadOnly}
      autoFocus={focusSearch}
      onSelect={() => {
        nextModeRef.current = 'summary'
      }}
      onTextCommit={() => {
        nextModeRef.current = 'manual'
      }}
    />
  )

  if (mode === 'search') {
    return (
      <div className="space-y-2">
        {search}
        {nameError}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
          <span>Vyhledejte kontakt, název firmy nebo IČ.</span>
          <Button type="button" variant="link" className="text-[13px]" onClick={() => setMode('manual')}>
            Zadat ručně
          </Button>
        </div>
      </div>
    )
  }

  if (mode === 'summary') {
    return <ParticipantSummary prefix={prefix} onEdit={() => setMode('manual')} onChange={clearParticipant} isReadOnly={isReadOnly} />
  }


  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {search}
        {nameError}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <InputGroup className="h-8">
          <InputGroupAddon>
            <InputGroupText className="text-sm">{t('businessId.label')}</InputGroupText>
          </InputGroupAddon>
          <InputGroupInput
            inputMode="numeric"
            readOnly={isReadOnly}
            {...register(`${prefix}.businessId`)}
          />
        </InputGroup>
        <InputGroup className="h-8">
          <InputGroupAddon>
            <InputGroupText>{t('taxId.label')}</InputGroupText>
          </InputGroupAddon>
          <InputGroupInput
            readOnly={isReadOnly}
            {...register(`${prefix}.taxId`)}
          />
        </InputGroup>
      </div>

      <div className="grid gap-2">
        <Input
          autoComplete="street-address"
          placeholder="Ulice a číslo popisné"
          readOnly={isReadOnly}
          {...register(`${prefix}.address.address`)}
        />
        <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2 sm:grid-cols-[7rem_minmax(0,1fr)_minmax(0,12rem)] [&_button[role=combobox]]:w-full">
          <Input
            autoComplete="postal-code"
            placeholder="PSČ"
            readOnly={isReadOnly}
            {...register(`${prefix}.address.zip`)}
          />
          <Input
            autoComplete="address-level2"
            placeholder="Město"
            readOnly={isReadOnly}
            {...register(`${prefix}.address.city`)}
          />
          <div className="col-span-2 sm:col-span-1">
            <CountrySelect control={methods.control as any} prefix={prefix} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <FormField
            control={methods.control}
            name={`${prefix}.email`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className={cn({
                      'border-danger':
                        errors?.[prefix] && !!errors?.[prefix]?.email,
                    })}
                    placeholder={t('email.placeholder')}
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {errors[prefix]?.email?.message && (
            <p className="text-sm text-danger">
              {t(`email.errors.invalid`)}
            </p>
          )}
        </div>

        <div>
          <FormField
            control={methods.control}
            name={`${prefix}.phone`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className={cn({
                      'border-danger':
                        errors?.[prefix] && !!errors?.[prefix]?.phone,
                    })}
                    placeholder={t('phone.placeholder')}
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {errors[prefix]?.phone?.message && (
            <p className="text-sm text-danger">
              {t(errors[prefix]?.phone?.message as 'phone.errors.invalid')}
            </p>
          )}
        </div>
      </div>

      {showAccountNumber && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <FormField
              control={methods.control}
              name={`${prefix}.accountNumber`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={cn({
                        'border-danger':
                          errors?.[prefix] && !!errors?.[prefix]?.accountNumber,
                      })}
                      placeholder={t('accountNumber.placeholder')}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {errors[prefix]?.accountNumber?.message && (
              <p className="text-sm text-danger">
                {t(
                  errors[prefix]?.accountNumber
                    ?.message as 'accountNumber.errors.invalid',
                )}
              </p>
            )}
          </div>

          <div>
            <FormField
              control={methods.control}
              name={`${prefix}.iban`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={cn({
                        'border-danger':
                          errors?.[prefix] && !!errors?.[prefix]?.iban,
                      })}
                      placeholder={t('iban.placeholder')}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {errors[prefix]?.iban?.message && (
              <p className="text-sm text-danger">
                {t(errors[prefix]?.iban?.message as 'iban.errors.invalid')}
              </p>
            )}
          </div>
        </div>
      )}

      {showDefaultBankAccount && bankAccounts && bankAccounts.length > 0 && (
        <FormField
          control={methods.control}
          name={`${prefix}.defaultBankAccountId` as any}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('defaultBankAccount.label')}</FormLabel>
              <Select
                value={field.value ?? ''}
                onValueChange={(value) => field.onChange(value || null)}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('defaultBankAccount.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id!}>
                      {account.name}
                      {account.accountNumber
                        ? ` (${account.accountNumber}/${account.bankCode ?? ''})`
                        : account.iban
                          ? ` (${account.iban})`
                          : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      )}

      {hasName && !hasDetailErrors && (
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => setMode('summary')}>
            Hotovo
          </Button>
        </div>
      )}
    </div>
  )
}

interface ParticipantSummaryProps {
  prefix: ParticipantFormProps['prefix']
  isReadOnly?: boolean
  onEdit: () => void
  onChange: () => void
}

/** Kompaktní karta vybraného kontaktu (jméno, adresa, IČO/DIČ, kontakt). */
const ParticipantSummary = ({ prefix, isReadOnly, onEdit, onChange }: ParticipantSummaryProps) => {
  const { control } = useFormContext<DocumentFormValues>()
  const party = useWatch({ control, name: prefix })
  const countries = useCountries()
  const address = party?.address
  const country = address?.country && address.country !== 'CZ'
    ? countries.find((c) => c.code === address.country)?.name ?? address.country
    : null
  const cityLine = [address?.zip, address?.city].filter(Boolean).join(' ')
  const addressLine = [address?.address, cityLine, country].filter(Boolean).join(', ')

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
      <div className="min-w-0 space-y-0.5 text-sm">
        <p className="truncate font-medium">{party?.name}</p>
        {addressLine && <p className="text-muted-foreground">{addressLine}</p>}
        {(party?.businessId || party?.taxId) && (
          <p className="flex flex-wrap gap-x-4 text-muted-foreground tabular">
            {party?.businessId && <span>IČO {party.businessId}</span>}
            {party?.taxId && <span>DIČ {party.taxId}</span>}
          </p>
        )}
        {(party?.email || party?.phone) && (
          <p className="flex flex-wrap gap-x-4 text-muted-foreground">
            {party?.email && <span className="truncate">{party.email}</span>}
            {party?.phone && <span className="tabular">{party.phone}</span>}
          </p>
        )}
      </div>
      {!isReadOnly && (
        <div className="-mr-2 flex shrink-0 items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
            <PencilIcon />
            Upravit
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onChange}>
            <RefreshCwIcon />
            Změnit
          </Button>
        </div>
      )}
    </div>
  )
}

ParticipantForm.displayName = 'ParticipantForm'
